import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { InputArrowsComponent } from "shared-ui-components/lines/inputArrowsComponent.js";
import * as react from "react";
export const ConflictingValuesPlaceholder = "—";
function GetCurrentNumericValue(value, props) {
    const numeric = parseFloat(value);
    if (!isNaN(numeric)) {
        return numeric;
    }
    if (props.placeholder !== undefined) {
        const placeholderNumeric = parseFloat(props.placeholder);
        if (!isNaN(placeholderNumeric)) {
            return placeholderNumeric;
        }
    }
    return 0;
}
function FormatValue(value, props) {
    if (props.numbersOnly) {
        if (!value) {
            value = "0";
        }
        //Removing starting zero if there is a number of a minus after it.
        if (value.search(/0+[0-9-]/g) === 0) {
            value = value.substring(1);
        }
    }
    if (props.numeric) {
        let numericValue = GetCurrentNumericValue(value, props);
        if (props.roundValues) {
            numericValue = Math.round(numericValue);
        }
        if (props.min !== undefined) {
            numericValue = Math.max(props.min, numericValue);
        }
        if (props.max !== undefined) {
            numericValue = Math.min(props.max, numericValue);
        }
        value = numericValue.toString();
    }
    return value;
}
/**
 * Used to allow the user to input text without updating the source on every keystroke.
 */
export class LazyTextInputLineComponent extends react.Component {
    // eslint-disable-next-line babylonjs/available
    constructor(props) {
        super(props);
        this._localChange = false;
        this._getInputValue = (props) => {
            const emptyValue = props.numeric ? "0" : "";
            const value = props.target[props.propertyName];
            return props.formatValue?.(value) ?? value ?? emptyValue;
        };
        this._validateInput = (value) => {
            if (this.props.extractValue) {
                try {
                    this.props.extractValue(value);
                }
                catch {
                    return false;
                }
            }
            return true;
        };
        this._onSubmit = (value) => {
            value = FormatValue(value, this.props);
            if (this._validateInput(value) === false) {
                if (this.props.onExtractValueFailed && this._lastInvalidSubmission !== value) {
                    this.props.onExtractValueFailed(value);
                }
                this._lastInvalidSubmission = value;
                return;
            }
            const valueToSet = this.props.extractValue ? this.props.extractValue(value) : value;
            const initialValue = this.props.target[this.props.propertyName];
            this.props.target[this.props.propertyName] = valueToSet;
            this.props.onSubmit?.();
            this.setState({
                originalInput: valueToSet,
            });
            if (this.props.onPropertyChangedObservable) {
                this.props.onPropertyChangedObservable.notifyObservers({
                    object: this.props.target,
                    property: this.props.propertyName,
                    initialValue,
                    value: valueToSet,
                });
            }
        };
        this._setInput = (input) => {
            if (this.props.disabled) {
                return;
            }
            if (this.props.numbersOnly) {
                if (/[^0-9.px%-]/g.test(input)) {
                    return;
                }
            }
            this.setState({
                input,
                inputValid: this._validateInput(input),
            });
        };
        this._setDragging = (dragging) => {
            this.setState({ dragging });
        };
        this._incrementValue = (amount) => {
            if (this.props.step) {
                amount *= this.props.step;
            }
            if (this.props.arrowsIncrement) {
                this.props.arrowsIncrement(amount);
            }
            const currentValue = GetCurrentNumericValue(this.state.input, this.props);
            const newValue = (currentValue + amount).toFixed(2);
            this._setInput(newValue);
            if (!this.props.arrowsIncrement) {
                this._onSubmit(newValue);
            }
        };
        this._onChange = (event) => {
            this._localChange = true;
            this._setInput(event.target.value);
        };
        this._onKeyDown = (event) => {
            if (!this.props.disabled) {
                if (event.key === "Enter") {
                    this._onSubmit(this.state.input);
                }
                if (this.props.arrows) {
                    if (event.key === "ArrowUp") {
                        this._incrementValue(1);
                        event.preventDefault();
                    }
                    if (event.key === "ArrowDown") {
                        this._incrementValue(-1);
                        event.preventDefault();
                    }
                }
            }
        };
        this._onBlur = () => {
            this._onSubmit(this.state.input);
            if (this.props.lockObject) {
                this.props.lockObject.lock = false;
            }
        };
        this._onFocus = () => {
            if (this.props.lockObject) {
                this.props.lockObject.lock = true;
            }
        };
        const originalInput = this._getInputValue(props);
        this.state = {
            input: originalInput,
            originalInput,
            dragging: false,
            inputValid: true,
        };
    }
    // eslint-disable-next-line babylonjs/available
    shouldComponentUpdate(nextProps, nextState) {
        if (this._localChange) {
            this._localChange = false;
            return true;
        }
        const newValue = this._getInputValue(nextProps);
        if (newValue !== nextState.originalInput) {
            nextState.originalInput = newValue;
            nextState.input = newValue;
            return true;
        }
        if (nextState.dragging != this.state.dragging) {
            return true;
        }
        return false;
    }
    /**
     * If this component is unmounted while editing, ensure we submit the value.
     */
    componentWillUnmount() {
        this._onBlur();
    }
    /**
     * Draw the component.
     * @returns the component's JSX element
     */
    render() {
        const value = this.state.input === ConflictingValuesPlaceholder ? "" : this.state.input;
        const placeholder = this.state.input === ConflictingValuesPlaceholder ? ConflictingValuesPlaceholder : this.props.placeholder || "";
        const step = this.props.step || (this.props.roundValues ? 1 : 0.01);
        const className = this.props.multilines ? "textInputArea" : this.props.unit !== undefined ? "textInputLine withUnits" : "textInputLine";
        const style = { background: this.state.inputValid ? undefined : "lightpink" };
        return (_jsxs("div", { className: className, children: [this.props.icon && _jsx("img", { src: this.props.icon, title: this.props.iconLabel, alt: this.props.iconLabel, color: "black", className: "icon" }), this.props.label !== undefined && (_jsx("div", { className: "label", title: this.props.label, children: this.props.label })), this.props.multilines && (_jsx(_Fragment, { children: _jsx("textarea", { className: this.props.disabled ? "disabled" : "", style: style, value: this.state.input, onBlur: this._onBlur, onFocus: this._onFocus, onChange: this._onChange, disabled: this.props.disabled }) })), !this.props.multilines && (_jsxs("div", { className: `value${this.props.noUnderline === true ? " noUnderline" : ""}${this.props.arrows ? " hasArrows" : ""}${this.state.dragging ? " dragging" : ""}`, children: [_jsx("input", { className: this.props.disabled ? "disabled" : "", style: style, value: value, onBlur: this._onBlur, onFocus: this._onFocus, onChange: this._onChange, onKeyDown: this._onKeyDown, placeholder: placeholder, type: this.props.numeric ? "number" : "text", step: step, disabled: this.props.disabled }), this.props.arrows && _jsx(InputArrowsComponent, { incrementValue: this._incrementValue, setDragging: this._setDragging })] })), this.props.unit] }));
    }
}
//# sourceMappingURL=lazyTextInputLineComponent.js.map